import { Injectable } from '@angular/core';
import {
  CashAccountId,
  DepositaryMobileBank,
  FavoriteNumbers,
  LoadingStatus,
  Performance,
  PerformanceFrequency,
  Portfolio,
  PortfolioNumber,
  Transaction
} from '@qvs/core';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { selectIsAuthenticated } from '@qvs/core/auth';
import { networkIsOnline, selectEnvironment } from '@qvs/core/env';
import {
  initialLoadPortfolios,
  loadDepositaryBanks,
  loadPortfoliosSuccess,
  loadPortfolioSuccess,
  selectPortfolioCurrent,
  setCreditCardPortfolioIds
} from '~/modules/portfolio/slice';
import { selectCurrentClient, selectUserProfile } from '@qvs/core/user';
import * as wizeMobileApi from '@wize-mobile-api';
import { PortfolioMapper } from '~/modules/portfolio/portfolio.mapper';
import { DateUtils } from '~/shared/utils/date.utils';
import { environment } from '#environment';

@Injectable({
  providedIn: 'root'
})
export class PortfolioService {
  private readonly creditCardPortfolioType = 'CC';
  private readonly lazyLoadItemsCount = 500;

  constructor(
    private readonly store: Store,
    private readonly wizeApi: wizeMobileApi.ThirdpartyMobileService,
    private readonly wizePortfolioMobileService: wizeMobileApi.PortfolioMobileService,
    private readonly valutationApi: wizeMobileApi.ValuationMobileService,
    private readonly accountMobileService: wizeMobileApi.AccountMobileService,
    private readonly performanceMobileService: wizeMobileApi.PerformanceMobileService
  ) {}

  public toggleFavorite = (portfolio: Portfolio, favoriteNumbers: FavoriteNumbers): Observable<FavoriteNumbers> =>
    favoriteNumbers.indexOf(portfolio.portfolioNumber) < 0 ? of([portfolio.portfolioNumber]) : of([]);

  public loadPortfolio = (portfolioNumber: PortfolioNumber): Observable<Portfolio> =>
    this.valutationApi
      .getValuationProductAllocationByPortfolioIdentification(portfolioNumber, true)
      .pipe(switchMap((allocation) => of(PortfolioMapper.fromWizeValuation(portfolioNumber, allocation))));

  public loadPortfolios(): Observable<Portfolio[]> {
    return this.wizePortfolioMobileService.listMobilePortfolios(true, false).pipe(
      map((portfolios) => this.removeClosedPortfolios(portfolios)),
      map((portfolios) => {
        const creditCardPortfolios = portfolios.filter((portfolio) => portfolio.portfolioTypeId === this.creditCardPortfolioType);
        const nonCreditCardPortfolios = portfolios.filter(
          (portfolio) => portfolio.portfolioTypeId !== this.creditCardPortfolioType
        );
        if (creditCardPortfolios.length > 0) {
          const creditCardPortfolioIds = creditCardPortfolios.map((portfolio) => portfolio.id);
          this.store.dispatch(setCreditCardPortfolioIds({ creditCardPortfolioIds }));
        }
        return nonCreditCardPortfolios;
      }),
      switchMap((wizePortfolios) => of(PortfolioMapper.fromWizePortfolios(wizePortfolios)))
    );
  }

  public loadTransactionsForCashAccount(cashAccountId: CashAccountId, transactionsCount?: number): Observable<Transaction[]> {
    return this.accountMobileService
      .getAccountLastCashTransactions(cashAccountId, transactionsCount || this.lazyLoadItemsCount)
      .pipe(switchMap((transactions) => of(PortfolioMapper.fromWizeCashAccountTransactions(transactions))));
  }

  public loadYtdPerformance = (portfolioNumber: PortfolioNumber): Observable<Performance> =>
    this.performanceMobileService
      .getYtdPerformance(portfolioNumber, true, PerformanceFrequency.MONTHLY)
      .pipe(switchMap((wizePerformance) => of(PortfolioMapper.fromWizePerformance(wizePerformance))));

  public loadPreviousYearPerformance = (portfolioNumber: PortfolioNumber): Observable<Performance> =>
    this.performanceMobileService
      .getPreviousYearPerformance(portfolioNumber, true, PerformanceFrequency.MONTHLY)
      .pipe(switchMap((wizePerformance) => of(PortfolioMapper.fromWizePerformance(wizePerformance))));

  public loadInceptionPerformance = (portfolioNumber: PortfolioNumber): Observable<Performance> =>
    this.performanceMobileService
      .getInceptionPerformance(portfolioNumber, true, PerformanceFrequency.ANNUAL)
      .pipe(switchMap((wizePerformance) => of(PortfolioMapper.fromWizePerformance(wizePerformance))));

  public loadDepositaryBanks = (): Observable<DepositaryMobileBank[]> =>
    this.wizeApi
      .listDepositaryBanks()
      .pipe(switchMap((depositaryBanks) => of(PortfolioMapper.fromWizeDepositaryBanks(depositaryBanks))));

  public init(): Promise<void> {
    this.observeListOfPortfolios();
    this.observeCurrentSelectedPortfolio();
    if (environment.featureFlags.isDepositaryBanksEnabled) {
      this.store.dispatch(loadDepositaryBanks());
    }
    return Promise.resolve();
  }

  private observeCurrentSelectedPortfolio() {
    /**
     * load current selected portfolio
     **/
    combineLatest([
      this.store.pipe(select(selectPortfolioCurrent)),
      this.store.pipe(select(selectIsAuthenticated)),
      this.store.pipe(select(selectEnvironment)),
      this.store.pipe(select(selectCurrentClient)),
      this.store.pipe(select(networkIsOnline))
    ])
      .pipe(
        filter(
          ([portfolio, token, env]) =>
            portfolio?.portfolioNumber && portfolio?.loadingStatus !== LoadingStatus.SUCCEEDED && !!token && !!env
        ),
        switchMap(([portfolio]) => this.loadPortfolio(portfolio.portfolioNumber)),
        filter((portfolio) => !!portfolio),
        tap((portfolio) => this.store.dispatch(loadPortfolioSuccess({ portfolio })))
      )
      .subscribe();
  }

  private observeListOfPortfolios() {
    /**
     * load list of portfolios
     */
    combineLatest([
      this.store.pipe(select(selectIsAuthenticated)),
      this.store.pipe(select(selectEnvironment)),
      this.store.pipe(select(selectUserProfile))
    ])
      .pipe(
        switchMap(([authenticated, env, userProfile]) => {
          if (authenticated && env && userProfile) {
            this.store.dispatch(initialLoadPortfolios());
            return this.loadPortfolios();
          }
          return of(null);
        }),
        filter((portfolios) => !!portfolios)
      )
      .subscribe((portfolios) => this.store.dispatch(loadPortfoliosSuccess({ portfolios })));
  }

  private readonly removeClosedPortfolios = (portfolios: wizeMobileApi.PortfolioMobile[]) =>
    portfolios.filter((portfolio) =>
      portfolio.closingDate !== undefined
        ? DateUtils.new(DateUtils.new(portfolio.closingDate).startOf('day').toDate()).isAfter(
            DateUtils.new().startOf('day').toDate()
          )
        : true
    );
}
